import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { supabase } from "../lib/supabase";
import { authService } from "../services/authService";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// ─── localStorage helpers ────────────────────────────────────────────────────
const LS_KEYS = {
  ALLOWED_ROUTES: "allowedRoutes",
  SELECTED_ORG_ID: "selectedOrgId",
  SELECTED_ORG_NAME: "selectedOrgName",
  USER_CATEGORY_ID: "userCategoryId",
  USER_CATEGORY_NAME: "userCategoryName",
  FIRST_BULK_UPLOAD: "firstBulkUpload",
};

const clearOrgContext = () => {
  Object.values(LS_KEYS).forEach((key) => localStorage.removeItem(key));
};

const persistOrgContext = ({
  orgId,
  orgName,
  categoryId,
  categoryName,
  firstBulkUpload,
  routes,
}) => {
  localStorage.setItem(LS_KEYS.SELECTED_ORG_ID, orgId);
  localStorage.setItem(LS_KEYS.SELECTED_ORG_NAME, orgName);
  localStorage.setItem(LS_KEYS.USER_CATEGORY_ID, String(categoryId));
  localStorage.setItem(LS_KEYS.USER_CATEGORY_NAME, categoryName);
  localStorage.setItem(LS_KEYS.ALLOWED_ROUTES, JSON.stringify(routes));
  localStorage.setItem(LS_KEYS.FIRST_BULK_UPLOAD, String(firstBulkUpload));
};

const restoreOrgContext = () => {
  const orgId = localStorage.getItem(LS_KEYS.SELECTED_ORG_ID);
  const orgName = localStorage.getItem(LS_KEYS.SELECTED_ORG_NAME);
  const categoryId = localStorage.getItem(LS_KEYS.USER_CATEGORY_ID);
  const categoryName = localStorage.getItem(LS_KEYS.USER_CATEGORY_NAME);
  const firstBulkUpload =
    localStorage.getItem(LS_KEYS.FIRST_BULK_UPLOAD) === "true"; // ← add
  const routes = JSON.parse(
    localStorage.getItem(LS_KEYS.ALLOWED_ROUTES) || "[]",
  );

  if (!orgId || !orgName || !categoryId) {
    clearOrgContext();
    return null;
  }

  return {
    orgId,
    orgName,
    categoryId: Number(categoryId),
    categoryName,
    firstBulkUpload, // ← add
    routes,
  };
};
// ─────────────────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ── Multi-org state ──
  const [memberships, setMemberships] = useState([]); // all orgs user belongs to
  const bootstrapCalledRef = useRef(false); // prevents double-call from checkInitialSession + SIGNED_IN

  const [selectedOrg, setSelectedOrg] = useState(null); // { orgId, orgName, categoryId, categoryName }
  const [needsOrgSelection, setNeedsOrgSelection] = useState(false);
  const [membershipsLoading, setMembershipsLoading] = useState(false);

  // ── Route permission state ──
  const [allowedRoutes, setAllowedRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(true);

  // ── MFA state ──
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);

  const isRecoveryPage = () => window.location.pathname === "/change-password";
  const isMFAResetPage = () =>
    window.location.pathname === "/security/mfa-reset";

  const loadProfile = useCallback((userId, orgId = null) => {
    const activeOrgId = orgId || localStorage.getItem("selectedOrgId");
    authService
      ?.getUserProfile(userId, activeOrgId)
      ?.then((profileData) => setProfile(profileData))
      ?.catch((err) => console.error("Profile load error:", err));
  }, []);

  // ─── Load routes for a specific user category ─────────────────────────────
  const loadRoutesByCategory = useCallback(async (userCategoryId) => {
    try {
      setRoutesLoading(true);
      const routes = await authService.getRoutesByCategory(userCategoryId);
      setAllowedRoutes(routes);
      return routes;
    } catch (err) {
      console.error("Route fetch error:", err);
      return [];
    } finally {
      setRoutesLoading(false);
    }
  }, []);

  // ─── Select / Switch Organization ─────────────────────────────────────────
  /**
   * Called from OrgSelectionPage (or auto-select for single-org users).
   * membership = { orgId, orgName, userCategoryId, categoryName }
   */
  const selectOrganization = useCallback(
    async (membership) => {
      try {
        setRoutesLoading(true);
        const routes = await authService.getRoutesByCategory(
          membership.userCategoryId,
        );

        const orgCtx = {
          orgId: membership.orgId,
          orgName: membership.orgName,
          categoryId: membership.userCategoryId,
          categoryName: membership.categoryName,
          firstBulkUpload: membership.firstBulkUpload,
          routes,
        };

        persistOrgContext(orgCtx);
        setSelectedOrg(orgCtx);
        setAllowedRoutes(routes);
        setNeedsOrgSelection(false);
        if (user?.id) {
          loadProfile(user.id, membership.orgId);
        }
      } catch (err) {
        console.error("Org selection error:", err);
      } finally {
        setRoutesLoading(false);
      }
    },
    [user, loadProfile],
  );

  /**
   * Called from Header org-switcher. Same as selectOrganization but also
   * navigates user back to dashboard (caller handles navigation).
   */
  const switchOrganization = useCallback(
    async (membership) => {
      clearOrgContext();
      setSelectedOrg(null);
      setAllowedRoutes([]);
      await selectOrganization(membership);
    },
    [selectOrganization],
  );

  // ─── Bootstrap memberships after login ────────────────────────────────────
  const bootstrapMemberships = useCallback(
    async (userId) => {
      // Guard: prevent double-call when both checkInitialSession and SIGNED_IN fire
      if (bootstrapCalledRef.current) {
        console.log(
          "[Auth] Bootstrap already in progress, skipping duplicate call",
        );
        return;
      }
      bootstrapCalledRef.current = true;

      try {
        setMembershipsLoading(true);

        // Clear any stale route data from the old auth system before fetching
        localStorage.removeItem("allowedRoutes");

        const list = await authService.getUserMemberships(userId);
        console.log("[Auth] Memberships fetched:", list);
        setMemberships(list);

        if (list.length === 0) {
          // No active memberships — clear everything
          setNeedsOrgSelection(false);
          setRoutesLoading(false);
          return;
        }

        if (list.length === 1) {
          // Auto-select single org — no selection screen needed
          const sole = list[0];
          const routes = await authService.getRoutesByCategory(
            sole.userCategoryId,
          );
          const orgCtx = {
            orgId: sole.orgId,
            orgName: sole.orgName,
            categoryId: sole.userCategoryId,
            categoryName: sole.categoryName,
            firstBulkUpload: sole.firstBulkUpload,
            routes,
          };
          persistOrgContext(orgCtx);
          setSelectedOrg(orgCtx);
          setAllowedRoutes(routes);
          setNeedsOrgSelection(false);
          setRoutesLoading(false);
          loadProfile(userId, sole.orgId);
        } else {
          // Multiple orgs — show selection screen
          setNeedsOrgSelection(true);
          setRoutesLoading(false);
        }
      } catch (err) {
        console.error("Membership bootstrap error:", err);
        bootstrapCalledRef.current = false; // allow retry on next login
        setRoutesLoading(false);
      } finally {
        setMembershipsLoading(false);
      }
    },
    [loadProfile],
  );

  // ─── Session & Activity ───────────────────────────────────────────────────
  useEffect(() => {
    let lastActivity = Date.now();
    let interval;
    let lastDbUpdate = 0;
    let lastValidation = 0;

    const updateActivity = async () => {
      if (isRecoveryPage()) return;
      if (isMFAResetPage()) return;
      lastActivity = Date.now();
      const now = Date.now();

      if (now - lastValidation > 10000) {
        lastValidation = now;
        await authService.validateSession();
      }

      if (now - lastDbUpdate > 15000) {
        lastDbUpdate = now;

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) return;

        const payload = JSON.parse(atob(session.access_token.split(".")[1]));

        await supabase
          .from("app_user_sessions")
          .update({ last_activity: new Date() })
          .eq("session_id", payload.session_id);
      }
    };

    const activityEvents = [
      "click",
      "mousemove",
      "keydown",
      "scroll",
      "mousedown",
      "touchstart",
    ];

    activityEvents.forEach((event) =>
      document.addEventListener(event, updateActivity, true),
    );

    const handleStorageEvent = async (e) => {
      if (isMFAResetPage()) return;
      if (e.key === "session_invalidated") {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          setUser(null);
          setProfile(null);
          clearOrgContext();
          setAllowedRoutes([]);
          setSelectedOrg(null);
          setMemberships([]);
          window.location.href = "/";
          return;
        }

        const token = session.access_token;
        const payload = JSON.parse(atob(token.split(".")[1]));
        const sessionId = payload.session_id;

        const { data } = await supabase
          .from("app_user_sessions")
          .select("is_active")
          .eq("session_id", sessionId)
          .single();

        if (!data?.is_active) {
          await supabase.auth.signOut();
          setUser(null);
          setProfile(null);
          clearOrgContext();
          setAllowedRoutes([]);
          setSelectedOrg(null);
          setMemberships([]);
          window.location.href = "/";
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    const checkInitialSession = async () => {
      if (isRecoveryPage()) {
        setLoading(false);
        setRoutesLoading(false);
        return;
      }

      if (isMFAResetPage()) {
        setLoading(false);
        setRoutesLoading(false);
        return;
      }

      const session = await authService.getSession();

      if (!session?.user) {
        setUser(null);
        setLoading(false);
        setRoutesLoading(false);
        return;
      }

      const token = session.access_token;
      const payload = JSON.parse(atob(token.split(".")[1]));
      const sessionId = payload.session_id;

      const { data } = await supabase
        .from("app_user_sessions")
        .select("is_active")
        .eq("session_id", sessionId)
        .single();

      if (!data?.is_active) {
        await supabase.auth.signOut();
        setUser(null);
        setProfile(null);
        setLoading(false);
        setRoutesLoading(false);
        return;
      }

      setUser(session.user);

      // ── Try to restore org context from localStorage ──
      const restored = restoreOrgContext();
      loadProfile(session.user.id, restored?.orgId);

      if (restored) {
        setSelectedOrg(restored);
        setAllowedRoutes(restored.routes);
        setRoutesLoading(false);
        setLoading(false);
        // Still refresh memberships list in background for the switcher
        authService
          .getUserMemberships(session.user.id)
          .then(setMemberships)
          .catch(console.error);
      } else {
        setLoading(false);
        await bootstrapMemberships(session.user.id);
      }
    };

    checkInitialSession();

    interval = setInterval(
      async () => {
        if (isRecoveryPage()) return;
        if (isMFAResetPage()) return;

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) return;

        const token = session.access_token;
        const payload = JSON.parse(atob(token.split(".")[1]));
        const sessionId = payload.session_id;

        const { data } = await supabase
          .from("app_user_sessions")
          .select("is_active")
          .eq("session_id", sessionId)
          .single();

        if (!data?.is_active) {
          await supabase.auth.signOut();
          return;
        }

        const diff = Date.now() - lastActivity;
        if (diff >= 15 * 60 * 1000) {
          await supabase.auth.signOut();
          return;
        }

        await supabase
          .from("app_user_sessions")
          .update({ last_activity: new Date() })
          .eq("session_id", sessionId);
      },
      50 * 60 * 1000,
    );

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (isRecoveryPage()) return;
        if (isMFAResetPage()) return;

        if (event === "SIGNED_OUT") {
          bootstrapCalledRef.current = false; // reset so next login triggers fresh bootstrap
          setUser(null);
          setProfile(null);
          clearOrgContext();
          setAllowedRoutes([]);
          setSelectedOrg(null);
          setMemberships([]);
          setNeedsOrgSelection(false);
          window.location.href = "/";
        }

        if (event === "SIGNED_IN" && session?.user) {
          setUser(session.user);
          // Check if we can restore from localStorage (e.g. token refresh)
          const restored = restoreOrgContext();
          loadProfile(session.user.id, restored?.orgId);
          if (restored) {
            setSelectedOrg(restored);
            setAllowedRoutes(restored.routes);
            setRoutesLoading(false);
            // Refresh memberships list in background for the switcher
            authService
              .getUserMemberships(session.user.id)
              .then(setMemberships)
              .catch(console.error);
          } else {
            // Fresh login — bootstrap org selection
            bootstrapMemberships(session.user.id);
          }
        }
      },
    );

    return () => {
      clearInterval(interval);
      ["click", "mousemove", "keydown", "scroll"].forEach((event) =>
        window.removeEventListener(event, updateActivity),
      );
      window.removeEventListener("storage", handleStorageEvent);
      authListener?.subscription?.unsubscribe();
    };
  }, [bootstrapMemberships]);

  // ─── Auth Actions ─────────────────────────────────────────────────────────
  const signIn = async (email, password) => {
    try {
      setError("");
      const result = await authService.signIn(email, password);

      if (result?.mustChangePassword) {
        return result;
      }

      const factors = await supabase.auth.mfa.listFactors();
      const hasMFA = factors?.data?.totp?.length > 0;

      // in signIn()
      const mfaEnabled = import.meta.env.VITE_ENABLE_MFA === "true";

      if (!mfaEnabled) {
        setMfaRequired(false);
        setMfaVerified(true);

        return {
          ...result,
          requiresMFA: false,
          setupMFA: false,
        };
      }

      if (hasMFA) {
        setMfaRequired(true);
        setMfaVerified(false);

        return {
          ...result,
          requiresMFA: true,
          setupMFA: false,
        };
      }

      setMfaRequired(false);
      setMfaVerified(false);
      return { ...result, requiresMFA: false, setupMFA: true };
    } catch (err) {
      setError(err?.message || "Login failed");
      throw err;
    }
  };
  const resendConfirmation = async (email) => {
    try {
      setError("");
      return await authService.resendConfirmation(email);
    } catch (err) {
      setError(err?.message || "Failed to resend confirmation email");
      throw err;
    }
  };
  const verifyMFA = async (code) => {
    // in signIn()
    const mfaEnabled = import.meta.env.VITE_ENABLE_MFA === "true";

    if (!mfaEnabled) {
      setMfaVerified(true);
      setMfaRequired(false);
      return true;
    }
    try {
      const factors = await supabase.auth.mfa.listFactors();
      const factor = factors.data.totp[0];

      const challenge = await supabase.auth.mfa.challenge({
        factorId: factor.id,
      });

      const verify = await supabase.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challenge.data.id,
        code,
      });

      if (verify.error) throw verify.error;

      setMfaVerified(true);
      setMfaRequired(false);

      return true;
    } catch (err) {
      throw err;
    }
  };

  const signUp = async (email, password, fullName, organization) => {
    try {
      setError("");
      const data = await authService?.signUp(
        email,
        password,
        fullName,
        organization,
      );
      return data;
    } catch (err) {
      setError(err?.message || "Failed to sign up");
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError("");
      await authService.signOut();
      setUser(null);
      setProfile(null);
      clearOrgContext();
      setAllowedRoutes([]);
      setSelectedOrg(null);
      setMemberships([]);
      setNeedsOrgSelection(false);
    } catch (err) {
      setError(err?.message || "Logout failed");
      throw err;
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError("");
      return await authService.forgotPassword(email);
    } catch (err) {
      setError(err?.message || "Failed to send reset email");
      throw err;
    }
  };
  const refreshProfile = async () => {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("*")
      .eq("id", user.id)
      .single();
    if (!error) setProfile(data);

    // ← Add this: re-fetch first_bulk_upload for the current org
    if (selectedOrg?.orgId) {
      const { data: membership } = await supabase
        .from("user_org_memberships")
        .select("first_bulk_upload")
        .eq("user_id", user.id)
        .eq("organization_id", selectedOrg.orgId)
        .single();

      if (membership) {
        const updated = {
          ...selectedOrg,
          firstBulkUpload: membership.first_bulk_upload,
        };
        setSelectedOrg(updated);
        localStorage.setItem(
          "firstBulkUpload",
          String(membership.first_bulk_upload),
        );
      }
    }
  };
  const changePassword = async (newPassword) => {
    try {
      setError("");
      const data = await authService.changePassword(newPassword);
      if (user?.id) {
        const profileData = await authService.getUserProfile(user.id);
        setProfile(profileData);
      }
      return data;
    } catch (err) {
      setError(err?.message || "Failed to change password");
      throw err;
    }
  };

  const value = {
    // Auth
    user,
    profile,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    forgotPassword,
    changePassword,
    resendConfirmation,
    // MFA
    mfaRequired,
    mfaVerified,
    verifyMFA,
    setMfaVerified,

    // Routes
    allowedRoutes,
    routesLoading,

    // Multi-Org
    memberships,
    membershipsLoading,
    selectedOrg,
    needsOrgSelection,
    selectOrganization,
    switchOrganization,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
